import React from "react";
import { useTranslation } from "react-i18next";

import { Box } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { Select } from "components/forms";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { CurrencyType } from "store/types";
import { getEndOfPeriod } from "utils/debt";
import toIsoMidday from "utils/to-iso-midday";

import { FormValues } from "../../schemas";
import { LoanAmount } from "../../form-fields/LoanAmount";
import { PaymentTerm } from "../../form-fields/PaymentTerm";
import { FREQUENCY_OPTIONS } from "../constants";
import { OptionCard } from "../OptionCard";
import { QuestionSlot } from "../QuestionSlot";

interface Step1Props {
  formik: FormikProps<FormValues>;
  dateFormat: string;
  currency: CurrencyType;
  isSimpleLoan: boolean;
  debtTypeChosen: boolean;
  setDebtTypeChosen: (v: boolean) => void;
  reveal: (name: string) => void;
  editMode?: boolean;
}

export const Step1DebtConfig: React.FC<Step1Props> = ({
  formik, dateFormat, currency, isSimpleLoan,
  debtTypeChosen, setDebtTypeChosen, reveal, editMode = false
}) => {
  const { t } = useTranslation("debt");
  const { values, setFieldValue } = formik;
  const showDates = debtTypeChosen && values.loan_amount > 0;

  return (
    <Box>
      <QuestionSlot visible={!editMode} label={t("wizard.debtConfig.typeQuestion")}>
        <OptionCard
          value="loan"
          selected={debtTypeChosen && values.is_withdrawal}
          onSelect={() => {
            setFieldValue("is_facility", true);
            setFieldValue("is_withdrawal", true);
            setFieldValue("overriding_parent", false);
            setDebtTypeChosen(true);
          }}
          title={t("wizard.debtConfig.simpleLoan")}
          subtitle={t("wizard.debtConfig.simpleLoanDesc")}
        />
        <OptionCard
          value="facility"
          selected={debtTypeChosen && values.is_facility && !values.is_withdrawal}
          onSelect={() => {
            setFieldValue("is_facility", true);
            setFieldValue("is_withdrawal", false);
            setDebtTypeChosen(true);
          }}
          title={t("wizard.debtConfig.creditFacility")}
          subtitle={t("wizard.debtConfig.creditFacilityDesc")}
        />
      </QuestionSlot>

      <QuestionSlot visible={debtTypeChosen} label={t("wizard.debtConfig.paymentTermsQuestion")}>
        <PaymentTerm formik={formik} showCustomIndexationOptions={false} />
      </QuestionSlot>

      <QuestionSlot
        visible={debtTypeChosen}
        label={values.is_facility && !values.is_withdrawal
          ? t("wizard.debtConfig.commitmentAmountQuestion")
          : t("wizard.debtConfig.loanAmountQuestion")}
      >
        <LoanAmount
          formik={formik}
          currency={currency}
          label={values.is_facility && !values.is_withdrawal
            ? t("wizard.debtConfig.commitmentAmountLabel")
            : t("wizard.debtConfig.loanAmountLabel")}
        />
      </QuestionSlot>

      <QuestionSlot visible={showDates} label={t("wizard.debtConfig.startDateQuestion")}>
        <DesktopDatePicker
          format={dateFormat}
          views={["year", "month", "day"]}
          label={t("wizard.debtConfig.startDateLabel")}
          value={values.start_date ? dayjs(values.start_date) : null}
          onChange={(v) => {
            if (v) {
              setFieldValue("start_date", toIsoMidday(v));
              if (isSimpleLoan && values.loan_status_frequency)
                setFieldValue("loan_first_status_date", getEndOfPeriod(v, values.loan_status_frequency));
              reveal("start_date");
            }
          }}
          sx={{ width: "100%" }}
        />
      </QuestionSlot>

      <QuestionSlot visible={showDates} label={t("wizard.debtConfig.maturityQuestion")}>
        <DesktopDatePicker
          format={dateFormat}
          views={["year", "month", "day"]}
          label={t("wizard.debtConfig.maturityLabel")}
          minDate={values.start_date ? dayjs(values.start_date) : undefined}
          value={values.maturity_date ? dayjs(values.maturity_date) : null}
          onChange={(v) => {
            if (v) {
              setFieldValue("maturity_date", toIsoMidday(v));
              reveal("maturity_date");
            }
          }}
          sx={{ width: "100%" }}
        />
      </QuestionSlot>

      <QuestionSlot visible={showDates} label={t("wizard.debtConfig.statusFrequencyQuestion")}>
        <Select
          name="loan_status_frequency"
          label={t("wizard.debtConfig.statusFrequencyLabel")}
          variant="outlined"
          fullWidth
          value={values.loan_status_frequency || "quarter"}
          items={FREQUENCY_OPTIONS}
          onChange={(e) => {
            setFieldValue("loan_status_frequency", e.target.value);
            if (isSimpleLoan && values.start_date)
              setFieldValue("loan_first_status_date", getEndOfPeriod(dayjs(values.start_date), e.target.value));
          }}
        />
      </QuestionSlot>

      {isSimpleLoan && (
        <QuestionSlot visible={showDates && !!values.loan_status_frequency} label={t("wizard.debtConfig.firstStatusDateQuestion")}>
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            label={t("wizard.debtConfig.firstStatusDateLabel")}
            value={values.loan_first_status_date ? dayjs(values.loan_first_status_date) : null}
            onChange={(v) => setFieldValue("loan_first_status_date", v?.isValid() ? toIsoMidday(v) : null)}
            sx={{ width: "100%" }}
            slotProps={{ textField: { helperText: t("wizard.common.autoCalculated") } }}
          />
        </QuestionSlot>
      )}
    </Box>
  );
};
